<template>
  <div v-if="isVisible" class="blog-banner-collapsible-wrapper mb-8">
    <div
      class="blog-banner-collapsible"
      :style="containerStyle"
      :class="{ 'is-open': isExpanded }"
    >
      <!-- Cabecera interactiva (siempre visible) -->
      <div
        class="blog-banner-header"
        role="button"
        tabindex="0"
        :aria-expanded="isExpanded"
        @click="toggle"
        @keydown.enter.prevent="toggle"
        @keydown.space.prevent="toggle"
      >
        <div class="blog-banner-left">
          <!-- Logo si está configurado -->
          <div
            v-if="props.banner?.Logo && props.banner?.ImagenLogo"
            class="blog-banner-logo-container"
            :style="logoBlockStyle"
          >
            <img
              :src="imagenIDtoSRC(props.banner.ImagenLogo)"
              alt="Logo"
              class="blog-banner-logo"
              :style="logoImgStyle"
            />
          </div>

          <!-- Título del Banner -->
          <h3
            v-if="props.banner?.Titulo?.Texto"
            class="blog-banner-title"
            :style="titleStyle"
          >
            {{ props.banner.Titulo.Texto }}
          </h3>
        </div>

        <!-- Flecha interactiva con rotación animada -->
        <div
          v-if="hasDescription"
          class="blog-banner-arrow"
          :class="{ 'rotated': isExpanded }"
          title="Desplegar / Contraer"
        >
          <v-icon :color="arrowColor" size="28">mdi-chevron-down</v-icon>
        </div>
      </div>

      <!-- Cuerpo desplegable con animación suave -->
      <v-expand-transition>
        <div v-show="isExpanded && hasDescription" class="blog-banner-body">
          <div class="blog-banner-divider"></div>
          <div
            class="blog-banner-text"
            :style="textStyle"
            v-html="formattedDescription"
          ></div>
        </div>
      </v-expand-transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useUtils } from '#imports'

const props = defineProps<{
  banner?: any
}>()

const { imagenIDtoSRC, textToHtml } = useUtils()

// Estado de colapso/expansión
const isExpanded = ref(Boolean(props.banner?.InicialmenteAbierto))

// Sincronizar si cambia la prop
watch(
  () => props.banner?.InicialmenteAbierto,
  (val) => {
    if (val !== undefined) isExpanded.value = Boolean(val)
  }
)

const toggle = () => {
  if (hasDescription.value) {
    isExpanded.value = !isExpanded.value
  }
}

// Visibilidad general
const isVisible = computed(() => {
  if (!props.banner) return false
  return Boolean(props.banner.Mostrar)
})

// Verifica si existe descripción para expandir
const hasDescription = computed(() => {
  return Boolean(props.banner?.Texto?.Texto && props.banner.Texto.Texto.trim().length > 0)
})

// Texto formateado seguro
const formattedDescription = computed(() => {
  const raw = props.banner?.Texto?.Texto || ''
  if (typeof textToHtml === 'function') {
    return textToHtml(raw)
  }
  return raw.replace(/\n/g, '<br>')
})

// Estilos del contenedor
const containerStyle = computed(() => {
  const fondo = props.banner?.FondoCSS
  const styles: Record<string, string> = {}

  if (fondo?.BackgroundColorGradient && fondo?.BackgroundColor) {
    styles.background = `linear-gradient(135deg, ${fondo.BackgroundColor} 0%, ${fondo.BackgroundColorGradient} 100%)`
  } else if (fondo?.BackgroundColor) {
    styles.backgroundColor = fondo.BackgroundColor
  } else {
    styles.backgroundColor = '#ffffff'
  }

  if (fondo?.BorderColor) {
    styles.borderColor = fondo.BorderColor
  }

  return styles
})

// Estilo del título
const titleStyle = computed(() => {
  const css = props.banner?.Titulo?.css
  if (!css) return {}
  return {
    color: css.Color || '#1a1a1a',
    fontFamily: css.Font || 'inherit',
    fontSize: css.Size || '1.35rem',
    fontWeight: css.Weight || 700,
    textAlign: (css.Align as any) || 'left',
    textShadow: css.BackgroundColor ? `2px 2px 4px ${css.BackgroundColor}` : 'none'
  }
})

// Color de la flecha
const arrowColor = computed(() => {
  return props.banner?.Titulo?.css?.Color || '#1a1a1a'
})

// Estilos del bloque de logo
const logoBlockStyle = computed(() => {
  const css = props.banner?.ImagenLogoBlock?.css
  const align = css?.Align || 'left'
  let justifyContent = 'flex-start'
  if (align === 'center') justifyContent = 'center'
  if (align === 'right') justifyContent = 'flex-end'

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent
  }
})

// Estilos de la imagen del logo
const logoImgStyle = computed(() => {
  const css = props.banner?.ImagenLogoBlock?.css
  const size = css?.Size || '55px'
  return {
    maxWidth: size,
    maxHeight: size,
    width: 'auto',
    height: 'auto',
    objectFit: 'contain' as const
  }
})

// Estilos del texto / descripción
const textStyle = computed(() => {
  const css = props.banner?.Texto?.css
  if (!css) return {}
  return {
    color: css.Color || '#4a5568',
    fontFamily: css.Font || 'inherit',
    fontSize: css.Size || '1rem',
    fontWeight: css.Weight || 400,
    textAlign: (css.Align as any) || 'left',
    textShadow: css.BackgroundColor ? `1px 1px 2px ${css.BackgroundColor}` : 'none'
  }
})
</script>

<style scoped>
.blog-banner-collapsible-wrapper {
  width: 100%;
}

.blog-banner-collapsible {
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 4px 20px -4px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.blog-banner-collapsible:hover {
  box-shadow: 0 8px 30px -4px rgba(0, 0, 0, 0.09);
  border-color: rgba(0, 0, 0, 0.12);
}

.blog-banner-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  cursor: pointer;
  user-select: none;
  background-color: transparent;
  transition: background-color 0.2s ease;
}

.blog-banner-header:hover {
  background-color: rgba(0, 0, 0, 0.015);
}

.blog-banner-header:focus-visible {
  outline: 2px solid var(--v-theme-primary, #1976d2);
  outline-offset: -2px;
}

.blog-banner-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
}

.blog-banner-logo-container {
  flex-shrink: 0;
}

.blog-banner-logo {
  display: block;
}

.blog-banner-title {
  margin: 0;
  line-height: 1.3;
  word-break: break-word;
}

.blog-banner-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.04);
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s ease;
  flex-shrink: 0;
  margin-left: 12px;
}

.blog-banner-header:hover .blog-banner-arrow {
  background: rgba(0, 0, 0, 0.08);
}

.blog-banner-arrow.rotated {
  transform: rotate(180deg);
}

.blog-banner-body {
  padding: 0 24px 20px 24px;
}

.blog-banner-divider {
  height: 1px;
  background-color: rgba(0, 0, 0, 0.06);
  margin-bottom: 16px;
}

.blog-banner-text {
  line-height: 1.7;
  word-break: break-word;
}

@media (max-width: 600px) {
  .blog-banner-header {
    padding: 14px 16px;
  }
  .blog-banner-body {
    padding: 0 16px 16px 16px;
  }
  .blog-banner-left {
    gap: 12px;
  }
}
</style>
